<template>
  <div
    class="app-dialer-call-status"
    :class="{ [`is-${size}`]: true }"
  >
    <div
      v-if="isInProgress"
      class="title"
    >
      <or-tooltip
        :content="message"
        placement="bottom"
      >
        {{ duration }}
      </or-tooltip>
    </div>

    <div
      v-else-if="isConnecting"
      class="message"
    >
      {{ $t('hitl.modules.dialer.connecting-phone-call') }}
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue-demi';
import { format } from 'date-fns';
import { OrTooltipV3 as OrTooltip } from '@onereach/ui-components-25';
import { TranslateResult } from 'vue-i18n';
import { SIZE_PROP } from '../../constants';

const LOCAL_STORAGE_KEY = 'APP_DIALER_CALL_STATUS';
const getCachedTimerStart = (currentSessionId: string) => {
  const timerCacheJson = localStorage.getItem(LOCAL_STORAGE_KEY);

  if (!timerCacheJson) {
    return;
  }

  const { sessionId, start } = JSON.parse(timerCacheJson) as { sessionId: string; start: number; };

  if (currentSessionId !== sessionId) {
    localStorage.removeItem(LOCAL_STORAGE_KEY);
    return;
  } else {
    return start;
  }
};

export default defineComponent({
  name: 'AppDialerCallStatus',
  components: { OrTooltip },
  props: {
    isConnecting: Boolean,
    isInProgress: Boolean,
    timestamp: {
      type: [String, Number],
      default: 0,
    },
    sessionId: {
      type: String,
      default: '',
    },
    size: SIZE_PROP,
  },
  data() {
    const timestamp = new Date(this.timestamp).getTime();
    const cachedStartTime = getCachedTimerStart(this.sessionId);
    return {
      timer: 0,
      durationInSeconds: Math.round((new Date().getTime() - (cachedStartTime || timestamp)) / 1000),
    };
  },
  computed: {
    duration(): string {
      return format(new Date(0).setSeconds(this.durationInSeconds), 'mm:ss');
    },

    message(): TranslateResult {
      const timestamp = format(new Date(this.timestamp), 'p');
      return this.$t('hitl.modules.dialer.call-started-at', { timestamp });
    },
  },
  watch: {
    isInProgress: {
      handler() {
        window.clearInterval(this.timer);

        if (this.isInProgress) {
          if (!this.durationInSeconds) {
            // the timer starts after the createdAt of the session
            // which is why we consider durationInSeconds == 0 the start
            this.storeTimerValue();
          }

          this.timer = window.setInterval(() => {
            this.durationInSeconds++;
          }, 1000);
        } else {
          this.durationInSeconds = 0;
        }
      },
      immediate: true,
    },
  },
  beforeDestroy() {
    window.clearInterval(this.timer);
  },

  methods: {
    storeTimerValue() {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify({
        sessionId: this.sessionId,
        start: Date.now(),
      }));
    },
  },
});
</script>

<style lang="scss" scoped>
@use '@/styles' as *;
@import '../../styles';

.app-dialer-call-status {
  --status-title-font-size: 1.5rem;
  --status-message-font-size: var(--fs-1);

  &.is-mobile {
    --status-title-font-size: 1.5rem;
    --status-message-font-size: 1rem;
    height: auto;
  }

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 3rem;
  min-width: 6rem;
  overflow: hidden;

  .title,
  .message {
    color: var(--or-c-inverse-on-surface);
  }

  .title {
    font-size: var(--status-title-font-size);
    font-weight: var(--fw-2);
    line-height: 1.5;
  }

  .message {
    @include text-overflow;
    max-width: 100%;
    font-size: var(--status-message-font-size);
    line-height: 1.5;
  }
}
</style>
